import { createContext, useContext, useEffect, useState } from "react";
import { UserCryptoWallet } from "../Models/UserWallet";
import WalletService from "../Services/Wallets/WalletService";

interface ContextProps {
    assets: UserCryptoWallet[];
    clisha?: UserCryptoWallet;
    currentAsset: UserCryptoWallet | undefined;
    refreshAssets: () => any;
    setCurrentAsset: (asset: UserCryptoWallet) => any;
    walletSynchronisation: boolean;
    refreshed: number;
    getGlobalBalance: () => number;
    getFiatBalance: () => number;

    getCryptoBalance: () => number;
}

const AssetsContext = createContext<ContextProps>({
    assets: [],
    clisha: undefined,
    currentAsset: undefined,
    refreshAssets: () => true,
    // eslint-disable-next-line
    setCurrentAsset: (val: UserCryptoWallet) => {
        return;
    },
    walletSynchronisation: false,
    refreshed: 0,
    getGlobalBalance: () => 0,
    getFiatBalance: () => 0,
    getCryptoBalance: () => 0,
});

export default function AssetsProvider({
    children,
}: {
    children: React.ReactNode;
}) {
    const [walletSynchronisation, setWalletSynchronisation] =
        useState<boolean>(false);
    const [nbCall, setNbCall] = useState<number>(0);
    const [assets, setAssets] = useState<UserCryptoWallet[]>([]);
    const [asset, setAsset] = useState<UserCryptoWallet | undefined>(undefined);

    const refreshAssets = async () => {
        setWalletSynchronisation(true);

        const data = await WalletService.getUserWallet();
        setWalletSynchronisation(false);
        setAssets(data);
        setNbCall(nbCall + 1);
    };

    useEffect(() => {
        if (nbCall === 0) {
            refreshAssets();
        }
    }, [nbCall]);

    const getGlobalBalance = (): number => {
        const total = assets.reduce(
            (prevTotal: number, asset: UserCryptoWallet) => {
                const current = asset.wallet.solde * asset.current_price;

                return prevTotal + current;
            },
            0,
        );

        return total;
    };
    const getFiatBalance = (): number => {
        const total = assets.reduce(
            (prevTotal: number, asset: UserCryptoWallet) => {
                const current =
                    asset.type === "fiat"
                        ? asset.wallet.solde * asset.current_price
                        : 0;

                return prevTotal + current;
            },
            0,
        );

        return total;
    };

    const getCryptoBalance = (): number => {
        const total = assets.reduce(
            (prevTotal: number, asset: UserCryptoWallet) => {
                const current =
                    asset.type === "crypto"
                        ? asset.wallet.solde * asset.current_price
                        : 0;

                return prevTotal + current;
            },
            0,
        );

        return total;
    };

    const clisha = assets.find((asset) => asset.abbreviation === "CLISHA");

    return (
        <AssetsContext.Provider
            value={{
                walletSynchronisation,
                assets,
                refreshAssets,
                getGlobalBalance,
                getFiatBalance,
                clisha,
                getCryptoBalance,
                refreshed: nbCall,
                currentAsset: asset,
                setCurrentAsset: setAsset,
            }}
        >
            {children}
        </AssetsContext.Provider>
    );
}

export const useAssets = () => useContext(AssetsContext);
